<template>
    <div 
        ref="container" 
        tabindex="0"
        class="lines-container" 
        :class="props.containerClass" 
        data-type="lines-container"
        @focusin="onFocusIn"
    >
        <slot />
    </div>
</template>

<script lang="ts" setup>
    import { provide, type PropType, type Ref, ref, onUnmounted } from "vue";
    import type { IRowsContainerExposed } from "./types";
    import { useApplicationStore } from "@/stores/Application";
    import { Skeleton } from "@/components/Skeleton/types";
    import { type RowConfig } from "@/components/RowView/types";

    const ApplicationStore = useApplicationStore();
    const { hideHeader } = ApplicationStore;

    const props = defineProps ({
        itemsProps: {
            type: (Array as unknown) as PropType<RowConfig[]>,
            required: false,
            default: () => {
                return [];
            }
        },
        containerClass: {
            type: String,
            required: false,
            default: ""
        },
        dataContainer: {
            type: String,
            required: false,
            default: "self"
        }
    });
    const { $eventBus } = useNuxtApp();
    const container: Ref<HTMLElement | null> = ref(null);
    const emit = defineEmits(['on-down', 'leave-down']);
    let focusTimerId: NodeJS.Timeout | number | null = null;
    
    const getCountLoadedRows = (): number | null => container.value !== null ? Array.from(container.value.children).length : null;
    const isEmpty = (): boolean => container.value !== null && container.value.children.length === 0;

    const _focusUpRows = (): void => {
        $eventBus.emit("key-up-rows");
    };
    const leaveDown = (): void => {
       emit("leave-down");
    };
    const focusFirstRow = (): void => {
        if (container.value === null) {
            return;
        }

        let element: HTMLElement | null = container.value.children[0] as HTMLElement;

        if (!element ) {
            return;
        }
        
        if (element && element?.id  === Skeleton.ID) {
            if (focusTimerId === null) {
                focusTimerId = setInterval(() => {
                    element = container.value?.children[0] as HTMLElement;
                    focusFirstRow();
                }, 1000);
            }
            return;
        } else {
            clearFocusTimer(focusTimerId);
            focusToRow(0);
        }
    };
    const focusLastRow = (): void => {
        if (container.value === null) {
            return;
        }
        const lastIndex = container.value.children.length - 1;
        const element: HTMLElement | null = container.value.children[lastIndex] as HTMLElement;

        if(!element ) {
            return;
        }
        
        focusToRow(lastIndex);
    };

    const clearFocusTimer = (timerId: NodeJS.Timeout | number | null): void => {
        if (timerId) {
            clearInterval(timerId);
            focusTimerId = null;
        }
    };

    const focusToRow = (index: number): void => {
        const element: Element | undefined = container.value?.children[index];
        if (element instanceof HTMLElement) {
            element.focus({preventScroll:true});
        } 
    }

    const getIndexRow = (rowid: string | undefined): number => {
        if (!rowid || container.value === null) {
            throw new Error(`Row id:${rowid}, container:${container.value}`);
        }
        const elementsArray = Array.from(container.value.children);
        return elementsArray.findIndex((item: Element) => item.id === rowid);
    };
    
    const onKeyDown = (event: KeyboardEvent | HTMLElement | null): void => {
        if (container.value === null) {
            return;
        }
        const element = event instanceof KeyboardEvent ? event.target as HTMLElement : event;

        if (element instanceof HTMLElement) {
            const rowid = element.dataset.rowid;
            const indexRow = getIndexRow(rowid);
            const nextRowId = indexRow + 1;
            const lastIndex = container.value.children.length - 1;

            if (nextRowId > lastIndex) {
                leaveDown();
            } else {
                focusToRow(nextRowId);
                emit('on-down');
            }
        }
    };
    provide('onKeyDown', onKeyDown);

    const onKeyUp = (event: KeyboardEvent | HTMLElement | null): void => {
        const element = event instanceof KeyboardEvent ? event.target as HTMLElement : event;
        if (element instanceof HTMLElement) {
            const rowid = element.dataset.rowid;
            const indexRow = getIndexRow(rowid);
            const prevRowId = indexRow - 1;

            if (prevRowId < 0) {
                _focusUpRows();
            } else {
                focusToRow(prevRowId);
            }
        }
    };
    const onFocusIn = (): void => {
        hideHeader();
    };
    provide('onKeyUp', onKeyUp);

    defineExpose<IRowsContainerExposed>({
        focusFirstRow,
        focusLastRow,
        getCountLoadedRows,
        focusToRow,
        isEmpty
    });

    onUnmounted(() => {
        clearFocusTimer(focusTimerId);
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
